import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import './regulation-scroller.css';
import './regulation-summary-fixed.css';
import './regulation-strip-adaptive.css';

/** Keep navigation within the space left between the project and account controls. */
export function RegulationScroller({ children, selectedId }: { children: ReactNode; selectedId?: number }) {
  const viewport = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });
  useLayoutEffect(() => {
    const node = viewport.current!, content = list.current!;
    const measure = () => {
      const next = { left: node.scrollLeft > 1, right: node.scrollWidth - node.clientWidth - node.scrollLeft > 1 };
      setEdges(old => old.left === next.left && old.right === next.right ? old : next);
    };
    const wheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) return;
      const delta = (Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY) * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? node.clientWidth : 1);
      if (delta < 0 && node.scrollLeft > 0 || delta > 0 && node.scrollLeft < node.scrollWidth - node.clientWidth - 1) {
        event.preventDefault(); node.scrollLeft += delta;
      }
    };
    const reveal = (target: Element) => {
      const rect = target.getBoundingClientRect(), area = node.getBoundingClientRect();
      if (rect.left < area.left + 4) node.scrollLeft -= area.left + 4 - rect.left;
      else if (rect.right > area.right - 4) node.scrollLeft += rect.right - area.right + 4;
    };
    const focused = (event: FocusEvent) => { if (event.target instanceof Element) reveal(event.target); };
    node.addEventListener('wheel', wheel, { passive: false });
    node.addEventListener('scroll', measure, { passive: true });
    node.addEventListener('focusin', focused);
    const selected = content.querySelector(`[data-regulation-id="${selectedId}"]`);
    const resize = new ResizeObserver(() => { if (selected?.isConnected) reveal(selected); measure(); });
    resize.observe(node);resize.observe(content);
    if (selected) reveal(selected);
    measure();
    return () => { resize.disconnect();node.removeEventListener('wheel', wheel);node.removeEventListener('scroll', measure);node.removeEventListener('focusin', focused); };
  }, [selectedId]);
  const move = (direction: number) => {
    const node = viewport.current;
    if (node) node.scrollBy({ left: direction * Math.max(120, node.clientWidth * .7), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  };
  return <div className={`regulation-scroller${edges.left || edges.right ? ' regulation-scroller--overflow' : ''}`}>
    <button type="button" className="regulation-scroller__arrow regulation-scroller__arrow--left" aria-label="Регламенты влево" title="Регламенты влево" disabled={!edges.left} onClick={() => move(-1)}><ChevronLeft size={16}/></button>
    <div className="regulation-scroller__viewport" ref={viewport} role="navigation" aria-label="Регламенты проекта">
      <ul className="submenu" ref={list}>{children}</ul>
    </div>
    <button type="button" className="regulation-scroller__arrow regulation-scroller__arrow--right" aria-label="Регламенты вправо" title="Регламенты вправо" disabled={!edges.right} onClick={() => move(1)}><ChevronRight size={16}/></button>
    <div className="assets-summary-slot regulation-scroller__summary" data-regulation-summary={selectedId}/>
  </div>;
}
